import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useUserStore } from '@/store/user'
import { fetchPaddleProducts } from '@/helpers/paddle'
import { sendEvent } from '@/helpers/cerebro'
import {
  defaultPaywallConfig,
  DEFAULT_PRICING_TIER,
} from '@/config/paywallConfig'
import {
  deepMerge,
  unwrapIapCatalog,
  resolvePaywallVariant,
  resolveDiscountId,
  validatePaywallVariant,
} from '@/helpers/paywallResolver'

const tryParseJSON = (raw, paramName = 'unknown') => {
  if (raw == null) return undefined
  if (typeof raw !== 'string') return raw
  try {
    return JSON.parse(raw)
  } catch (err) {
    console.warn(
      `[paywall] Failed to parse Codeway parameter '${paramName}' as JSON; ignoring. Raw: ${String(raw).slice(0, 80)}`,
    )
    return undefined
  }
}

export const useProductStore = defineStore('product', () => {
  const userStore = useUserStore()

  const productsMap = ref({})
  const productKeysMap = ref({})

  const mergedStructure = ref(defaultPaywallConfig.paywallStructure)
  const mergedIadCatalog = ref(defaultPaywallConfig.iadCatalog)

  const buildMergedConfig = () => {
    const remoteStructure = tryParseJSON(
      userStore.getParameter('paywall_structure'),
      'paywall_structure',
    )
    const remoteIapRaw = tryParseJSON(
      userStore.getParameter('iap_catalog'),
      'iap_catalog',
    )
    const remoteIadCatalog = tryParseJSON(
      userStore.getParameter('iad_catalog'),
      'iad_catalog',
    )

    const remoteIapCatalog = unwrapIapCatalog(remoteIapRaw)

    const structure = deepMerge(
      defaultPaywallConfig.paywallStructure,
      remoteStructure,
    )
    const iapCatalog = deepMerge(
      defaultPaywallConfig.iapCatalog,
      remoteIapCatalog,
    )
    const iadCatalog = deepMerge(
      defaultPaywallConfig.iadCatalog,
      remoteIadCatalog,
    )
    const tier = userStore.getParameter('pricing_tier') || DEFAULT_PRICING_TIER

    return { structure, iapCatalog, iadCatalog, tier }
  }

  const fetchVariants = async ({ structure, iapCatalog, tier }) => {
    for (const variantName in structure) {
      const variant = structure[variantName]
      const warnings = validatePaywallVariant(variant, variantName)
      warnings.forEach(w => console.warn('[paywall config]', w))

      const resolved = resolvePaywallVariant(
        structure,
        iapCatalog,
        variantName,
        tier,
        DEFAULT_PRICING_TIER,
      )
      const validResolved = resolved.filter(r => r.priceId)
      const dropped = resolved.filter(r => !r.priceId).map(r => r.productKey)
      if (dropped.length) {
        console.warn(
          `[paywall] Dropped products with no resolvable priceId in '${variantName}': ${dropped.join(', ')}`,
        )
      }

      if (validResolved.length === 0) {
        console.warn(
          `[paywall] No resolvable products for variant '${variantName}', skipping`,
        )
        continue
      }

      const fetchItems = validResolved.map(r => ({
        quantity: 1,
        priceId: r.priceId,
      }))

      try {
        const paddleResponse = await fetchPaddleProducts(fetchItems)
        productsMap.value[variantName] = paddleResponse
        productKeysMap.value[variantName] = validResolved.map(r => r.productKey)
      } catch (err) {
        console.warn(
          `[paywall] Failed to fetch Paddle products for '${variantName}':`,
          err,
        )
      }
    }
  }

  const fetchProductsInternal = async () => {
    await userStore.getCurrentUser()
    sendEvent({ eventName: 'loadProducts' })

    const { structure, iapCatalog, iadCatalog, tier } = buildMergedConfig()
    mergedStructure.value = structure
    mergedIadCatalog.value = iadCatalog

    await fetchVariants({ structure, iapCatalog, tier })

    if (!productsMap.value.default || productsMap.value.default.length === 0) {
      throw new Error('No default products resolved; falling back to static')
    }
  }

  const fetchDefaultProducts = async () => {
    mergedStructure.value = defaultPaywallConfig.paywallStructure
    mergedIadCatalog.value = defaultPaywallConfig.iadCatalog

    await fetchVariants({
      structure: defaultPaywallConfig.paywallStructure,
      iapCatalog: defaultPaywallConfig.iapCatalog,
      tier: DEFAULT_PRICING_TIER,
    })

    if (!productsMap.value.default) {
      productsMap.value.default = []
      productKeysMap.value.default = []
    }
  }

  const refetchProducts = async type => {
    if (type === 'default') {
      await fetchDefaultProducts()
      return
    }
    try {
      await fetchProductsInternal()
    } catch (err) {
      console.warn('[paywall] Falling back to static defaults:', err.message)
      await fetchDefaultProducts()
    }
  }

  const getProductsWithSource = source => {
    if (
      productsMap.value[source] !== undefined &&
      productsMap.value[source].length !== 0
    ) {
      return productsMap.value[source]
    }
    return productsMap.value.default ?? []
  }

  const getProductKeysForSource = source => {
    if (productKeysMap.value[source]?.length) {
      return productKeysMap.value[source]
    }
    return productKeysMap.value.default ?? []
  }

  const getVariantStructure = source => {
    return mergedStructure.value?.[source] ?? mergedStructure.value?.default
  }

  const getDiscountIdForProductKey = (source, productKey) => {
    const variant = getVariantStructure(source)
    if (!variant?.products || !variant?.discountIds) return ''
    const idx = variant.products.indexOf(productKey)
    if (idx === -1) return ''
    const discountKey = variant.discountIds[idx]
    return resolveDiscountId(discountKey, mergedIadCatalog.value)
  }

  const getDiscountRateForProductKey = (source, productKey) => {
    const variant = getVariantStructure(source)
    if (!variant?.products || !variant?.discountRates) return 0
    const idx = variant.products.indexOf(productKey)
    if (idx === -1) return 0
    return variant.discountRates[idx] ?? 0
  }

  // Resolves a structure-level index field (most_popular / selected_index)
  // onto the surviving products array. If the target tier failed to resolve,
  // falls back to 0 so the badge / default-selected lands on a real product.
  const productIndexFor = (source, field) => {
    const variant = getVariantStructure(source)
    const targetKey = variant?.products?.[variant?.[field] ?? 0]
    if (!targetKey) return 0
    const surviving =
      productKeysMap.value[source] ?? productKeysMap.value.default ?? []
    const mapped = surviving.indexOf(targetKey)
    return mapped >= 0 ? mapped : 0
  }

  const getMostPopularIndex = source => productIndexFor(source, 'most_popular')
  const getDefaultSelectedIndex = source =>
    productIndexFor(source, 'selected_index')

  return {
    getProductsWithSource,
    getProductKeysForSource,
    getDiscountIdForProductKey,
    getDiscountRateForProductKey,
    getMostPopularIndex,
    getDefaultSelectedIndex,
    refetchProducts,
  }
})
